import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { HiOutlineComputerDesktop, HiOutlineDevicePhoneMobile, HiXMark } from 'react-icons/hi2';
import {
  ChatRequestModalOverlay,
  ChatRequestModalContent,
  ChatRequestModalHeader,
  ChatRequestModalTitle,
  ChatRequestModalCloseButton,
} from '../../../styles/UserWorkspacePageStyles';
import {
  ensureE2E,
  getLocalDeviceId,
  listDevices,
  revokeDevice,
  revokeOtherDevices,
  subscribeE2EChanged,
} from '../../../utils/e2e';
import { useT } from '../../../LocaleContext';

const sectionTitle = {
  margin: '18px 0 10px',
  fontSize: '0.82em',
  fontWeight: 600,
  letterSpacing: '0.02em',
  color: 'var(--accent, #6ea8fe)',
  opacity: 0.95,
  textAlign: 'left',
};

const muted = {
  margin: 0,
  color: 'inherit',
  opacity: 0.62,
  fontSize: '0.84em',
  lineHeight: 1.4,
  textAlign: 'left',
};

const rowStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  padding: '10px 0',
  width: '100%',
  textAlign: 'left',
};

const iconWrap = {
  width: 42,
  height: 42,
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  background: 'rgba(110, 168, 254, 0.18)',
  color: 'var(--accent, #6ea8fe)',
  fontSize: '1.15em',
};

const dangerBtn = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  width: '100%',
  margin: '4px 0 0',
  padding: '10px 0',
  border: 'none',
  background: 'transparent',
  color: '#e57373',
  font: 'inherit',
  fontWeight: 600,
  cursor: 'pointer',
  textAlign: 'left',
};

const ghostX = {
  border: 'none',
  background: 'transparent',
  color: 'inherit',
  opacity: 0.45,
  cursor: 'pointer',
  padding: 6,
  marginLeft: 'auto',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '1.1em',
  flexShrink: 0,
};

const formatWhen = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const now = new Date();
    const sameDay = d.toDateString() === now.toDateString();
    if (sameDay) {
      return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
    return d.toLocaleDateString();
  } catch (_) {
    return iso;
  }
};

const deviceIcon = (device) => {
  const platform = String(device?.platform || '').toLowerCase();
  if (platform === 'mobile' || /iphone|android|ios/i.test(device?.label || '')) {
    return <HiOutlineDevicePhoneMobile aria-hidden />;
  }
  return <HiOutlineComputerDesktop aria-hidden />;
};

const DevicesModal = ({ isOpen, onClose }) => {
  const t = useT();
  const [devices, setDevices] = useState([]);
  const [localId, setLocalId] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      await ensureE2E({ forceSync: true });
      const id = await getLocalDeviceId();
      setLocalId(id);
      const rows = await listDevices();
      setDevices(rows);
    } catch (err) {
      setError(err?.response?.data?.msg || err?.message || 'failed to load sessions');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isOpen) return undefined;
    refresh();
    return subscribeE2EChanged(() => {
      getLocalDeviceId().then(setLocalId).catch(() => {});
      listDevices().then(setDevices).catch(() => {});
    });
  }, [isOpen, refresh]);

  const { current, others } = useMemo(() => {
    const cur = devices.find((d) => d.is_current || d.device_id === localId) || null;
    const rest = devices
      .filter((d) => d !== cur && !(d.is_current || d.device_id === localId))
      .sort((a, b) => String(b.last_seen_at || '').localeCompare(String(a.last_seen_at || '')));
    return { current: cur, others: rest };
  }, [devices, localId]);

  if (!isOpen) {
    return null;
  }

  const onRevoke = async (deviceId) => {
    const isCurrent = deviceId === localId;
    const ok = window.confirm(
      isCurrent
        ? 'Terminate this session? Encryption keys on this browser will be cleared.'
        : 'Terminate this session?',
    );
    if (!ok) return;
    setBusyId(deviceId);
    setError('');
    try {
      await revokeDevice(deviceId);
      if (isCurrent) {
        onClose();
        return;
      }
      await refresh();
    } catch (err) {
      setError(err?.response?.data?.msg || err?.message || 'failed to terminate');
    } finally {
      setBusyId(null);
    }
  };

  const onRevokeOthers = async () => {
    if (!others.length) return;
    const ok = window.confirm('Terminate all other sessions?');
    if (!ok) return;
    setBusyId('__others__');
    setError('');
    try {
      await revokeOtherDevices();
      await refresh();
    } catch (err) {
      setError(err?.response?.data?.msg || err?.message || 'failed to terminate others');
    } finally {
      setBusyId(null);
    }
  };

  const renderDetails = (device, { showIp = true } = {}) => {
    const when = formatWhen(device.last_seen_at);
    const locBits = [];
    if (showIp && device.ip_address) locBits.push(device.ip_address);
    if (when) locBits.push(when);
    return (
      <div style={{ minWidth: 0, flex: 1, textAlign: 'left' }}>
        <div style={{ fontWeight: 650, fontSize: '1.02em', lineHeight: 1.25, textAlign: 'left' }}>
          {device.label || 'device'}
        </div>
        <div style={{ ...muted, marginTop: 3 }}>
          {device.client || 'Lynx Web'}
        </div>
        {locBits.length ? (
          <div style={{ ...muted, marginTop: 2 }}>
            {locBits.join(' · ')}
          </div>
        ) : null}
      </div>
    );
  };

  return (
    <ChatRequestModalOverlay onClick={onClose}>
      <ChatRequestModalContent
        onClick={(e) => e.stopPropagation()}
        style={{
          maxWidth: 420,
          width: '92%',
          maxHeight: '86vh',
          overflowY: 'auto',
          textAlign: 'left',
          alignItems: 'stretch',
        }}
      >
        <ChatRequestModalHeader>
          <div />
          <ChatRequestModalTitle>{t('modals.activeSessions')}</ChatRequestModalTitle>
          <ChatRequestModalCloseButton type="button" onClick={onClose}>
            ×
          </ChatRequestModalCloseButton>
        </ChatRequestModalHeader>

        {error ? (
          <p style={{ ...muted, color: '#e57373', marginBottom: 8 }}>{error}</p>
        ) : null}
        {loading && !devices.length ? (
          <p style={muted}>{t('common.loading')}</p>
        ) : null}

        <div style={sectionTitle}>{t('modals.thisDevice')}</div>
        {current ? (
          <div style={{ ...rowStyle, paddingTop: 4 }}>
            <div style={iconWrap}>{deviceIcon(current)}</div>
            {renderDetails(current)}
          </div>
        ) : (
          <p style={muted}>{loading ? '…' : 'no local device yet'}</p>
        )}

        {others.length ? (
          <>
            <button
              type="button"
              style={dangerBtn}
              onClick={onRevokeOthers}
              disabled={busyId === '__others__'}
            >
              {busyId === '__others__' ? '…' : t('modals.terminateOthers')}
            </button>
            <p style={{ ...muted, marginTop: 2, marginBottom: 4 }}>
              Logs out all devices except for this one.
            </p>
          </>
        ) : null}

        <div style={sectionTitle}>{t('modals.activeDevices')}</div>
        {!others.length ? (
          <p style={{ ...muted, marginBottom: 8 }}>
            No other devices are linked to this account.
          </p>
        ) : (
          others.map((device) => (
            <div
              key={device.device_id}
              style={{
                ...rowStyle,
                borderTop: '1px solid rgba(127,127,127,0.18)',
              }}
            >
              <div style={iconWrap}>{deviceIcon(device)}</div>
              {renderDetails(device)}
              <button
                type="button"
                style={ghostX}
                title={t('modals.terminateSession')}
                aria-label={t('modals.terminateSession')}
                disabled={busyId === device.device_id}
                onClick={() => onRevoke(device.device_id)}
              >
                {busyId === device.device_id ? '…' : <HiXMark />}
              </button>
            </div>
          ))
        )}

        <p style={{ ...muted, marginTop: 16, marginBottom: 4 }}>
          Devices listed here can decrypt your end-to-end messages. Terminate any you do not recognize.
        </p>
      </ChatRequestModalContent>
    </ChatRequestModalOverlay>
  );
};

export default DevicesModal;
